import Vue from 'vue'
import store from '@/store/store';
import VueIntercom from 'vue-intercom';
import { appInsights } from '../../main.js';
Vue.use(VueIntercom, { appId: 'zetpu2p7' });
let thisVue = new Vue();

// ************************************************************************
// A user has an account (userDetails)
// Inside the user account (userDetails) we have the suppliers that this user has access to (userAccountTypes)
// If the user Fabio manages two suppliers like "Algarexperience" and "SuperBalloonTrips" the array userAccountTypes would have these two suppliers
// A user can only manage one supplier at a time so the selected supplier is the one on (userAccountTypeDefault)
// To change the settings of a user on a given supplier (i.e: change filters on calendar view)
// all we need is get it with the getter "getCurrentAccountType" change it and send it via "updateSupplierSettings"
// ************************************************************************

// Role map to translate the local accessType into the name expected by the backend RBAC
const ROLE_MAP = {
    admin: 'Administrator',
    basic: 'Basic',
    restricted: 'Restricted',
    viewer: 'Viewer',
    myTeamSupervisor: 'SupervisorMyTeam',
    marketingSupervisor: 'SupervisorMarketing',
    mainSupervisor: 'SupervisorPrimary',
    agentSupervisor: 'SupervisorAgent',
    financial: 'Financial'
}

function resolveBackendRole(localCode) {
    return ROLE_MAP[localCode] ? ROLE_MAP[localCode] : null;
}

const state = {
    messages: [],
    isTryingLogin: false,
    accountSetup: [],
    defaultRedirect: 'dashboard',
    userDetails: null,
    account: {
        name: '',
        email: '',
        supplierId: null,
        agentId: null,
    },
    accessTypes: [
        {
            id: 0,
            label: 'Basic',
            code: 'basic'
        },
        {
            id: 1,
            label: 'Admin',
            code: 'admin'
        },
        {
            id: 2,
            label: 'Restricted',
            code: 'restricted'
        },
        {
            id: 3,
            label: 'Main Supervisor',
            code: 'mainSupervisor',
            group: 'supervisor'
        },
        {
            id: 4,
            label: 'Agent Supervisor',
            code: 'agentSupervisor',
            group: 'supervisor'
        },
        {
            id: 5,
            label: 'Marketing Supervisor',
            code: 'marketingSupervisor',
            group: 'supervisor'
        },
        {
            id: 6,
            label: 'My Team Supervisor',
            code: 'myTeamSupervisor',
            group: 'supervisor'
        },
        {
            id: 7,
            label: 'Viewer',
            code: 'viewer'
        },
        {
            id: 10,
            label: 'Financial',
            code: 'financial'
        },
    ],
    savedDefaultCalendarViewValue: false,
    isUpdatingSupplierSettings: false,
    canReadInternalNote: null,
    canEditInternalNote: null,
}

const getters = {
    getSavedDefaultCalendarViewValue: state => state.savedDefaultCalendarViewValue,
    getIsTryingLogin: state => state.isTryingLogin,
    getUserDetails: state => state.userDetails,
    getCurrentAccountType: state => {
        if (!state.userDetails || !state.userDetails.userAccountTypes) {
            return [];
        } else {
            return state.userDetails.userAccountTypes.find(accountType => {
                return accountType.supplierId == state.userDetails.userAccountTypeDefault.supplierId;
            });
        }
    },
    accountSetup: state => state.accountSetup,
    getMessages: state => state.messages,
    account: state => state.account,
    getActiveIntegrations: state => {
        if (!state.account.supplier) {
            return {};
        }

        return {
            getYourGuide: store.getters.getAllIntegrations.find(i => i.id == 202) && store.getters.getAllIntegrations.find(i => i.id == 202).integrated == true ? true : false,
            viator: store.getters.getAllIntegrations.find(i => i.id == 203) && store.getters.getAllIntegrations.find(i => i.id == 203).integrated == true ? true : false
        }
    },
    getUserAccessTypes: state => state.accessTypes,
    getUserAccessType: (state) => {
        function getAccessObject(accessId) {
            return state.accessTypes.filter(accessType => {
                return accessType.id == accessId;
            })[0];
        }

        if (state.account.accessType == undefined) {
            return getAccessObject(2); // Basic user id = 2
        } else {
            let testArr = state.accessTypes.filter(accessType => {
                return accessType.id == state.account.accessType;
            })

            if (testArr.length > 0) {
                return getAccessObject(state.account.accessType);
            } else {
                return getAccessObject(2);
            }
        }
    },
    getUpdatingSupplierSettings: state => state.isUpdatingSupplierSettings,
    getCanReadInternalNote: state => state.canReadInternalNote,
    getCanEditInternalNote: state => state.canEditInternalNote,
};

const actions = {
    // Logs a user in both from the API and the client
    login: ({ state, dispatch }, data) => {
        let params = data.params;
        let query = data.query;

        state.isTryingLogin = true;

        return axios.post(axios.authURL + 'auth/login', params).then((response) => {
            state.isTryingLogin = false;
            if (response && response.status === 200 && response.data.access_token) {
                localStorage.setItem('pluralo_token', response.data.access_token);

                //show loading screen
                dispatch('switchLayout', 'Loading');
                dispatch('checkUser').then(r => {
                    if (r != 'agent') {
                        // redirects to the correct view, hiding the loading screen
                        Vue.router.push({ name: query.redirect ? query.redirect : state.defaultRedirect });
                    }

                    if (r == 'revokedByApi') {
                        // redirects to the correct view, hiding the loading screen
                        Vue.router.push({ name: 'auth.login' });
                    }
                    
                });
                return true;
            }
            return false;
        }).catch((error) => {
            state.isTryingLogin = false;
            Vue.toasted.error(error.response.data.error_description, { className: 'auth-alert' });
        });
    },

    // Logs a user out both from the API and the client
    logout: ({ dispatch }) => {
        if (appInsights && process.env.NODE_ENV === 'production') {
            appInsights.clearAuthenticatedUserContext();
        }

        axios.get(axios.authURL + 'auth/logout').then((response) => {
            if (response && response.status === 200) {
                dispatch('handleLogout');
            }
        }).catch(function (error) {
            Vue.toasted.error(error.response.data.message, { className: 'auth-alert' });
        });
    },

    handleLogout: ({ commit, dispatch }) => {
        // Clear the last-customer-details from localStorage on logout
        localStorage.removeItem('last-customer-details');
        
        dispatch('clearAllNotifications');
        dispatch('clientLogout');
        commit('CLEAR_CHECK_NOTIFICATIONS_INTERVAL');
    },

    // Logs a user out - client-side only (no API check)
    clientLogout: ({ commit, dispatch }) => {
        localStorage.removeItem('pluralo_token');
        thisVue.$intercom.shutdown();
        thisVue.$intercom.boot();

        dispatch('closeModal');
        commit('CLEAR_ACCOUNT');

        Vue.router.push({ name: 'auth.login' });
    },

    // Registers an agent
    register: ({ commit }, fields) => {
        return axios.post(axios.authURL + 'auth/registerAgent', fields).then((response) => {
            if (response && response.status === 200) {
                Vue.router.push({ name: 'auth.login', params: { validateAccount: true } });
            }
            
            return response;
        }).catch(function (error) {
            return error;
        });
    },

    // Registers a supplier
    registerSupplier: ({ commit }, fields) => {
        return axios.post(axios.authURL + 'auth/registerSupplier', fields.parsed).then((response) => {
            if (response && response.status === 200) {
                Vue.router.push({ name: 'auth.login', params: { validateAccount: true } });
                Vue.toasted.success(response.data.message, { className: 'auth-alert' });
            }
            return response
        }).catch(function (error) {
            Vue.toasted.error(error.response.data.message, { className: 'auth-alert' });
            return error;
        });
    },

    // Gets all messages and notifications for the current user
    fetchMessages: ({ commit }) => {
        var messages = [
            {
                id: 1,
                title: 'Request for information',
                intro: 'Do you have 10PAX availability for Cruise tomorrow 10:00?',
                description: 'Big group wants to go for a cruise tommorrow.',
                user: 'Anne Smith',
                isRead: false
            },
            {
                id: 2,
                title: 'PROMO10 not working',
                intro: 'Trying to insert promo10 but not working',
                description: 'Hi there, not sure what happen, is promo10 still available?',
                user: 'John Marshall',
                isRead: true
            },
        ];

        commit('GET_MESSAGES', messages);
    },

    // Change the settings of this user for this supplier
    updateSupplierSettings: ({ commit, state }, payload) => {
        let userDetails = JSON.parse(JSON.stringify(state.userDetails));
        state.isUpdatingSupplierSettings = true;

        // 1 - find correct index
        let foundItem = userDetails.userAccountTypes.find((userAccountType, index) => userAccountType.supplierId == payload.supplierId);
        let index = userDetails.userAccountTypes.indexOf(foundItem);

        // 2 - replace the correct index with the new payload
        userDetails.userAccountTypes[index] = payload;

        let params = {
            by: state.account.email,
            supplierId: payload.supplierId,
            userAccount: userDetails
        }

        return axios.post(axios.authURL + 'user/settings/update', params).then((response) => {
            commit('CHANGE_SAVED_DEFAULT_CALENDAR_VIEW_VALUE', params.userAccount.userAccountTypeDefault.defaultCalendarView);

            if (response.data.isSuccess) {
                Vue.toasted.success('Settings Saved');
            }

            state.isUpdatingSupplierSettings = false;
            return response;
        }).catch(function (error) {
            state.isUpdatingSupplierSettings = false;
            return error;
        });
    },


    // Takes a new message object and adds it to the list
    addMessage: ({ commit }, message) => {
        commit('ADD_NEW_MESSAGE', message);
    },

    // Sets up the account form fields in a common object
    updateAccountSetup: ({ commit, state }, fields) => {
        commit('UPDATE_FIELDS', { object: 'accountSetup', fields: fields });
    },

    // Resets the account setup object for agents and suppliers
    resetAccountSetup: ({ commit, dispatch }, type) => {
        let reset = {};

        if (type === 'agent') {
            reset = {
                email: '',
                password: '',
                name: '',
                companyName: '',
                imageLogoBase64: null,
                step: null,
                type: type,
            };
        }
        else {
            reset = {
                email: '',
                password: '',
                country: null,
                currency: null,
                step: null,
                type: type,
            };
        }

        commit('RESET_ACCOUNT_SETUP', reset);
        dispatch('clearAllNotifications');
    },

    async checkUser({ state, commit, dispatch }, payload) {
        if (localStorage.getItem('pluralo_token')) {
            return axios.get('api/user/GetUserDetails').then(response => {
                if (response.data) {

                    if (response.data.userAccountTypeDefault.accountType == 1) {
                        dispatch('agentAccess', payload);
                        return 'agent';
                    }

                    if (response.data.isActive == false) {
                        dispatch('inactiveUserAccess');
                        return 'inactive';
                    }

                    if (response.data.userAccountTypeDefault.accountType == 2) {
                        const defaultHomePageValue = response.data.userAccountTypes[0].defaultHomePage;

                        if(defaultHomePageValue === 0) {
                            commit('SET_DEFAULT_REDIRECT', 'dashboard');
                        } else if (defaultHomePageValue === 1) {
                            commit('SET_DEFAULT_REDIRECT', 'calendar');
                        } else if (defaultHomePageValue === 2) {
                            commit('SET_DEFAULT_REDIRECT', 'reservations');
                        }

                        commit('SET_USER_DETAILS', response.data);
                        dispatch('supplierAccess', response);
                        return 'supplier';
                    }
                } else {
                    if (response.serverInfo.isToShowServerInfo == true) {
                        localStorage.clear();
                        return 'revokedByApi';
                    }
                }
            }).catch(err => {
                return err;
            });
        } else {
            return 'notLogged';
        }
    },

    inactiveUserAccess({ state, commit, dispatch }) {
        localStorage.clear();
        Vue.toasted.error('Account inactive');
        Vue.router.push({ name: 'auth.login' });
    },

    loadInternalNotePermission: async ({ state, commit, getters }) => {
        if (state.canEditInternalNote !== null && state.canReadInternalNote !== null) return

        try {
            const res = await axios.get('/api/rbac/rules');
            const rules = Array.isArray(res && res.data) ? res.data : [];
            const backendRole = resolveBackendRole(getters.getUserAccessType.code);
            let canRead = false;
            let canWrite = false;

            for (let i = 0; i < rules.length; i++) {
                const ent = rules[i];
                if (!ent || ent.entity !== 'Booking') continue;

                const props = ent.Properties || []
                for (let j = 0; j < props.length; j++) {
                    const p = props[j];
                    
                    if (!p || p.Property !== 'InternalNote') continue;
                    
                    if (Array.isArray(p.AllowRead) && backendRole) {
                        canRead = p.AllowRead.indexOf(backendRole) !== -1;
                    }

                    if (Array.isArray(p.AllowWrite) && backendRole) {
                        canWrite = p.AllowWrite.indexOf(backendRole) !== -1;
                    }
                }
            }

            commit('SET_CAN_READ_INTERNAL_NOTE', canRead);
            commit('SET_CAN_EDIT_INTERNAL_NOTE', canWrite);
        } catch(e) {
            // On error we mark as false (no permission)
            commit('SET_CAN_READ_INTERNAL_NOTE', false);
            commit('SET_CAN_EDIT_INTERNAL_NOTE', false);
        }
    },

    supplierAccess({ state, commit, dispatch }, payload) {
        const data = {
            id: payload.data.id,
            email: payload.data.email,
            name: payload.data.name,
            accessType: payload.data.userAccountTypeDefault.accessType,
            accountType: payload.data.userAccountTypeDefault.accountType,
            supplierId: payload.data.userAccountTypeDefault.supplierId,
            agentId: payload.data.userAccountTypeDefault.agentId,
            supplier: payload.data.userAccountTypeDefault.supplier,
            agent: payload.data.userAccountTypeDefault.agent,
            avatar: payload.data.imagePhoto,
            salesChannel: payload.data.salesChannel,
            defaultHomePage: payload.data.userAccountTypeDefault.defaultHomePage
        };

        dispatch('setSupplierAccountAsSelected', payload.data.userAccountTypeDefault);
        commit('UPDATE_ACCOUNT', data);

        let downloads = async () => {
            await dispatch('downloadSupplierIntegrations').then(integrationsArray => {
                let foundInvoiceSystemIntegration = integrationsArray.find(integration => integration.integrationType == 3 && integration.integrationStatus == 1)
                if (foundInvoiceSystemIntegration) {
                    return dispatch('downloadInvoicingIntegrationDetails', foundInvoiceSystemIntegration.integration)
                }
            });
            await dispatch('loadInternalNotePermission')
            await dispatch('initSettings')
            await dispatch('donwloadSubscribedPlugins')
            await dispatch('downloadEventColors')
            await dispatch('downloadAudienceTypeList')
            await dispatch('initExtras')
            await dispatch('initCategories')
            await dispatch('downloadSalesChannels')
            await dispatch('downloadSlimFilters')
            await dispatch('downloadRoutes')
            await dispatch('downloadAgents')
            await dispatch('downloadTeam')
            await dispatch('downloadOrigins')
            await dispatch('downloadSegments')
            await dispatch('initReasons')
            await dispatch('fetchCustomBusinessTypes')
        }

        downloads().then(r => {
            return 'supplier'
        });
    },

    agentAccess({ dispatch }, payload) {
        if (payload && payload.route && payload.route.path == "/logout") {
            dispatch('logout');
        } else {
            window.location.href = process.env.AGENT_PORTAL_URL + "/?token=" + localStorage.getItem('pluralo_token');
        }
    },

    // Tests if the user is logged in or not by retrieving his token
    refreshAccountData({ commit, dispatch }, routeName) {
        return axios.get('api/user/GetUserDetails').then(user => {
            if (user.data.isActive == false) {
                localStorage.clear();
                Vue.toasted.error('Account inactive');
                Vue.router.push({ name: 'auth.login' });
            }

            if (user.data.userAccountTypeDefault.accountType == 1) {
                window.location.href = process.env.AGENT_PORTAL_URL + "/?token=" + localStorage.getItem('pluralo_token');
                
                return user;
            }

            commit('SET_USER_DETAILS', user.data);

            const data = {
                id: user.data.id,
                email: user.data.email,
                name: user.data.name,
                accessType: user.data.userAccountTypes[0].accessType,
                accountType: user.data.userAccountTypes[0].accountType,
                supplierId: user.data.userAccountTypes[0].supplierId,
                agentId: user.data.userAccountTypes[0].agentId,
                supplier: user.data.userAccountTypes[0].supplier,
                agent: user.data.userAccountTypeDefault.agent,
                avatar: user.data.imagePhoto,
                salesChannel: user.data.salesChannel,
            };
            
            commit('UPDATE_ACCOUNT', data);
            dispatch('downloadSlimFilters');
            
            // Re-evaluate permissions (e.g., if the role changed)
            dispatch('loadInternalNotePermission');

            return user;
        }).catch(error => {
        })
    },
}

const mutations = {
    SET_DEFAULT_REDIRECT: (state, redirect) => {
        state.defaultRedirect = redirect;
    },
    
    SET_USER_DETAILS: (state, userDetails) => {
        state.userDetails = userDetails;
    },

    UPDATE_ACCOUNT: (state, fields) => {
        Object.keys(fields).forEach((key) => {
            Vue.set(state.account, key, fields[key]);
        });
    },

    CLEAR_ACCOUNT: (state) => {
        state.account = {};
        state.canReadInternalNote = null;
        state.canEditInternalNote = null;
    },

    GET_MESSAGES: (state, messages) => {
        state.messages = messages;
    },

    ADD_NEW_MESSAGE: (state, message) => {
        state.messages.push(message);
    },

    UPDATE_FIELDS: (state, data) => {
        if (state[data.object] === undefined || !state[data.object]) {
            state[data.object] = {};
        }

        Object.keys(data.fields).forEach((key) => {
            Vue.set(state[data.object], key, data.fields[key]);
        });
    },

    RESET_ACCOUNT_SETUP: (state, fields) => {
        state.accountSetup = fields;
    },
    
    CHANGE_SAVED_DEFAULT_CALENDAR_VIEW_VALUE: (state, value) => {
        state.savedDefaultCalendarViewValue = value;
    },

    SET_CAN_READ_INTERNAL_NOTE: (state, val) => {
        state.canReadInternalNote = !!val;
    },

    SET_CAN_EDIT_INTERNAL_NOTE: (state, val) => {
        state.canEditInternalNote = !!val;
    },
}

export default {
    state,
    getters,
    actions,
    mutations
}



// WEBPACK FOOTER //
// ./src/store/modules/user.js